<script setup lang="ts">
  import { FontIcon, Illustration, PageIntro } from '@tundr/ds-components';
  import { useLocale } from '@tundr/i18n';
  import wizardHeaderLogo from '@tundr/theme-assets/illustrations/tundr-icon-logo.png';
  import { TUNDR_ICONS } from '@tundr/theme-assets/tundr-icons/tundr-icons-icons';
  import { computed } from 'vue';
  import { useWizardStore } from '../../store/wizard-stepper.store';
  import {
    actionClass,
    checkBadgeIconClass,
    counterClass,
    wizardDescriptionClass,
    wizardIntroClass,
  } from './WizardHeader.css';
  import type { WizardHeaderProps } from './WizardHeader.types';

  const props = defineProps<WizardHeaderProps>();

  const { t } = useLocale('wizard-stepper');

  const stepperStore = useWizardStore();

  const isInitialized = computed(() =>
    Boolean(stepperStore.steppers[props.namespace]),
  );

  const activeStep = computed(() => {
    const stepper = stepperStore.steppers[props.namespace];
    return stepper?.steps[stepper.activeIndex];
  });

  const descriptionComponent = computed(
    () => activeStep.value?.descriptionComponent,
  );

  const isRecapStep = computed(() => activeStep.value?.isRecap ?? false);

  const totalSteps = computed(
    () =>
      stepperStore.steppers[props.namespace]?.steps.filter(
        (step) => !step.isRecap,
      ).length ?? 0,
  );

  const currentStep = computed(() => {
    const stepper = stepperStore.steppers[props.namespace];
    if (!stepper) {
      return 0;
    }
    return stepper.steps
      .slice(0, stepper.activeIndex + 1)
      .filter((step) => !step.isRecap).length;
  });
</script>

<template>
  <PageIntro
    :class="wizardIntroClass"
    :title="title"
    :subtitle="description"
    :data-testid="testId"
    centered
  >
    <template #topWidget>
      <FontIcon
        v-if="isRecapStep"
        :icon-name="TUNDR_ICONS.CHECK_CIRCLE_SOLID"
        :class="checkBadgeIconClass"
      />
      <Illustration v-else :src="wizardHeaderLogo" :width="20" :height="35" />
    </template>

    <template v-if="descriptionComponent" #subtitle>
      <div :class="wizardDescriptionClass">
        <component :is="descriptionComponent" />
      </div>
    </template>

    <template v-if="isInitialized && !isRecapStep" #stepper>
      <p
        :class="counterClass"
        :data-testid="testId ? `${testId}-counter` : undefined"
      >
        <b> {{ t('counter.label') }} {{ currentStep }} </b>
        <span>{{ t('counter.separator') }} {{ totalSteps }}</span>
      </p>
    </template>

    <template v-if="$slots.action" #extraContent>
      <div :class="actionClass">
        <slot name="action" />
      </div>
    </template>
  </PageIntro>
</template>
